<script lang="ts">
  /**
   * "Join a game": the outlined sibling of the `StartTile`s, for the thing
   * that is not starting anything (typing a friend's code). An accent
   * outline on the ground, an icon, a title and a line, and a chevron.
   * TwinTen's together-tab join tile.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  import Icon from './Icon.svelte';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    /** The glyph, 22px, in `currentColor`; the shell's `join` by default. */
    readonly icon?: Snippet | undefined;
    readonly disabled?: boolean;
    readonly onclick: () => void;
    readonly testid?: string | undefined;
  }
  const { title, detail, icon, disabled = false, onclick, testid }: Props = $props();
</script>

<button type="button" class="ui-join-tile" {disabled} data-testid={testid} {onclick}>
  <span class="ui-join-tile-icon" aria-hidden="true">
    {#if icon !== undefined}{@render icon()}{:else}<Icon name="join" size={22} />{/if}
  </span>
  <span class="ui-join-tile-text">
    <span class="ui-join-tile-title">{title}</span>
    {#if detail !== undefined}
      <span class="ui-join-tile-detail">{detail}</span>
    {/if}
  </span>
  <span class="ui-join-tile-go" aria-hidden="true"><Icon name="chevronRight" size={20} /></span>
</button>

<style>
  .ui-join-tile {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3.75rem;
    padding: 0.75rem 1rem;
    border: 2px solid var(--ui-accent);
    border-radius: var(--ui-radius-md);
    background: transparent;
    color: var(--ui-ink);
    font-family: var(--ui-font);
    text-align: left;
  }

  .ui-join-tile:disabled {
    opacity: 0.55;
  }

  .ui-join-tile:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-join-tile-icon,
  .ui-join-tile-go {
    flex: 0 0 auto;
    display: inline-flex;
    color: var(--ui-accent);
  }

  .ui-join-tile-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-join-tile-title {
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-join-tile-detail {
    font: var(--ui-text-label);
    font-weight: 400;
    color: var(--ui-ink-2);
  }
</style>
